













































import {Component, Mixins, Prop, Watch} from 'vue-property-decorator'
import Translation from '@/mixins/Translation'
import PopupHandler from '@/mixins/popupHandler'
import FooterFixedHandler from '@/mixins/FooterFixedHandler'
import toCurrency from '@/filters/to-currency'
import MainLayout from '@/layouts/default/MainLayout.vue'
import HeaderLayout from '@/components/Header.vue'
import FooterLayout from '@/components/Footer.vue'
import FooterButton from '@/components/FooterButton.vue'
import TitleAndMessage from '@/components/TitleAndMessage.vue'
import ConfirmListItem from '@/pages/issue/components/ConfirmListItem.vue'
import ToggleCollapseButton from '@/pages/issue/components/ToggleCollapseButton.vue'
import ConfirmForm from '@/pages/issue/components/ConfirmForm.vue'
import PreviewPopup from '@/components/popup/InvoiceDocument.vue'
import DonePopup from '@/pages/issue/popup/Done.vue'
import CollapseTransition from 'element-ui/lib/transitions/collapse-transition'
import {cloneDeep, sum} from 'lodash'
import {DateTime} from 'luxon'
import {getBaseUrl} from '@/utils'
import {issueDateFormat, confirmFormat} from '@/pages/issue/constants/issue-date-format'
import {initExtraData} from '@/pages/issue/constants/init-extra-data'
import {Client} from '@/store/modules/clients/types'
import {Status, ExtraData, IssuePayload, GetInvoiceDocumentRequest} from '@/store/modules/issue/types'
import {
  UserPropertyPayload,
  BizPropertyPayload,
  LogEventWidthGroupsPayload,
} from '@/store/modules/gtm/types'
import {namespace} from 'vuex-class'

const issue = namespace('issue')
const biz = namespace('biz')
const clients = namespace('clients')
const layout = namespace('layout')
const userAgent = namespace('userAgent')
const gtm = namespace('gtm')

interface ConfirmItem {
  checkDate: ConfirmItemValue
  checkBiz: ConfirmItemValue
  checkClient: ConfirmItemValue
  checkClientContact: ConfirmItemValue
  checkItems: ConfirmItemValue
}

interface ConfirmItemValue {
  title: string
  description: string
  status: Status | 'loading'
}

@Component({
  components: {
    ToggleCollapseButton, [CollapseTransition.name]: CollapseTransition,
    TitleAndMessage, ConfirmListItem, ConfirmForm, PreviewPopup, DonePopup,
    MainLayout, HeaderLayout, FooterLayout, FooterButton,
  },
})
export default class IssueConfirm extends Mixins(Translation, PopupHandler, FooterFixedHandler) {
  @Prop({default: 'terms', type: String}) translation: string
  @Prop({default: 0, type: Number}) windowScrollY: number
  @Prop({default: false, type: Boolean}) done: boolean

  @issue.State('writeDate') writeDate: string
  @issue.State('items') items: any[]
  @issue.State('clientId') clientId: string
  @issue.State('extraData') stateExtraData: ExtraData
  @issue.State('copyInvoiceId') copyInvoiceId: string
  @issue.Getter('totalAmount') totalAmount: number
  @issue.Getter('confirmWriteDateStatus') confirmWriteDateStatus: Status
  @issue.Getter('confirmBizStatus') confirmBizStatus: Status
  @issue.Getter('confirmClientStatus') confirmClientStatus: Status
  @issue.Getter('confirmClientContactStatus') confirmClientContactStatus: Status
  @issue.Getter('confirmItemsStatus') confirmItemsStatus: Status
  @issue.Getter('isTaxFree') isTaxFree: boolean
  @issue.Getter('gtmCommonInfo') gtmCommonInfo: object
  @issue.Mutation('update') update: (payload: any) => void
  @issue.Action('getPreview') getPreview: (payload: GetInvoiceDocumentRequest) => Promise<string>
  @issue.Action('issueInvoice') issueInvoice: (payload: IssuePayload) => Promise<string>

  @biz.State('id') bizId: string
  @biz.State('name') bizName: string
  @biz.State('ownerName') bizOwnerName: string
  @biz.Action('getBiz') getBiz: () => Promise<void>

  @clients.State('client') client: Client
  @clients.Action('getClient') getClient: (payload: {clientId: string}) => Promise<void>

  @layout.Mutation('updateError') updateError: (payload: {id, message}) => void

  @userAgent.Getter('isMobile') isMobile: boolean

  @gtm.Action('logEventWithGroups') logEventWithGroups: (payload: LogEventWidthGroupsPayload) => void
  @gtm.Action('setUserProperty') setUserProperty: (payload: UserPropertyPayload) => void
  @gtm.Action('setBizProperty') setBizProperty: (payload: BizPropertyPayload) => void

  loadingStatus = {
    getClient: true,
    getBiz: true,
    issue: false,
  }
  visibleExtraData: boolean = false

  extraData: ExtraData = initExtraData
  previewData: string = ''

  get invoiceName(): string {
    const {ts, isTaxFree} = this
    return isTaxFree ? `${ts('tax-free-invoice')}` : `${ts('invoice')}`
  }

  get isAvailableIssue(): boolean {
    return this.confirmWriteDateStatus !== 'danger' &&
      this.confirmBizStatus !== 'danger' &&
      this.confirmClientStatus !== 'danger' &&
      this.confirmItemsStatus !== 'danger' &&
      !this.isTotalAmountError &&
      !this.loadingStatus.issue
  }

  get isEnteredExtraData(): boolean {
    return Object.keys(this.extraData).some((key) => {
      if(key === 'purposeType' && this.extraData[key] !== '청구') {
        return true
      }
      return key !== 'purposeType' && this.extraData[key]
    })
  }

  get issuePayload(): IssuePayload {
    return {
      writeDate: this.writeDate,
      receiver: this.clientId,
      ...this.extraData,
      detail: this.items,
    }
  }

  get totalItemName(): string {
    const {ts} = this
    const [firstItem = {itemName: ''}] = this.items
    const itemsCountText = this.items.length > 1 ? `외 ${this.items.length - 1}${ts('count')}` : ''
    return [firstItem?.itemName, itemsCountText].join(' ')
  }

  get writeDateToFormat(): string {
    return this.writeDate ? DateTime.fromFormat(this.writeDate, issueDateFormat).toFormat(confirmFormat) : ''
  }

  get contactToTextList(): any[] {
    const {ts} = this
    return [this.client.phoneNumber ? `${ts('sms')}` : null, this.client.email||this.client.emailSub ? `${ts('email')}` : null]
  }

  get confirmList(): ConfirmItem {
    const {ts} = this
    const requiredMessage = `${ts('input')}이 ${ts('need')}합니다`
    return {
      checkDate: {
        title: `${ts('write-date')}`,
        description: this.writeDateToFormat || requiredMessage,
        status: this.confirmWriteDateStatus,
      },
      checkClient: {
        title: `${ts('client')} ${ts('biz')} ${ts('info')}`,
        description: this.clientId ?
          `${this.client.name} ${this.client.ownerName}` : requiredMessage,
        status: this.loadingStatus.getClient ? 'loading' : this.confirmClientStatus,
      },
      checkItems: {
        title: `${ts('item')}`,
        description: this.items.length ?
          `${this.totalItemName} ${toCurrency(this.totalAmount)}${ts('currency')}` : requiredMessage,
        status: this.confirmItemsStatus,
      },
      checkClientContact: {
        title: `${ts('client')} ${ts('alarm')}`,
        description: this.clientId ? this.contactToTextList.filter((m) => m).join(', ') || `${ts('client')}에 ${ts('alarm')}을 보내지 않습니다` : requiredMessage,
        status: this.confirmClientContactStatus,
      },
      checkBiz: {
        title: `${ts('my')} ${ts('biz')} ${ts('info')}`,
        description: `${this.bizName} ${this.bizOwnerName || ''}`,
        status: this.loadingStatus.getBiz ? 'loading' : this.confirmBizStatus,
      },
    }
  }

  get amountDataSumValue(): number {
    const amountDataKeys = ['cash', 'checkBill', 'credit', 'note']
    return sum(amountDataKeys.map((key) => this.extraData[key]))
  }

  get isTotalAmountError(): boolean {
    return this.amountDataSumValue !== 0 && this.amountDataSumValue !== this.totalAmount
  }

  @Watch('isEnteredExtraData', {immediate: true})
  __isEnteredExtraData(value) {
    if(value) {
      this.visibleExtraData = true
    }
  }

  created() {
    this.loadBiz()
    this.loadClient()
    this.extraData = cloneDeep(this.stateExtraData)
  }

  onClickBackward() {
    this.update({extraData: this.extraData})
    this.$emit('action', 'back')
  }

  onClickDone() {
    if(this.loadingStatus.issue) return
    this.loadingStatus.issue = true
    this.issueInvoice(this.issuePayload)
      .then(() => {
        this.openPopup('done')
        this.$emit('done')
        this.logEventWithGroups({
          eventType: 'complete',
          eventProperties: {
            ...this.gtmCommonInfo,
            'complete_type': 'success',
            'invoice_duplicated': Boolean(this.copyInvoiceId),
            'status': this.extraData.purposeType,
          }
        })

        const addCount = 1
        const userAndBizProperty = {'invoice_issue_count': addCount}
        this.setUserProperty({
          event: 'addUserProperty',
          userProperty: userAndBizProperty
        })
        this.setBizProperty({
          event: 'addBizProperty',
          bizProperty: userAndBizProperty
        })
      })
      .catch((error) => {
        this.loadingStatus.issue = false
        this.updateError({
          id: 'create-issue',
          message: `${this.ts('issue')}에 ${this.ts('failed')}하였습니다.`,
        })
        throw error
      })
  }

  onConfirm(confirm) {
    if(this.loadingStatus.issue) return
    this.update({extraData: this.extraData})
    this.$emit('action', confirm)
  }

  onSendAmplitudeExtraData(inputType) {
    this.logEventWithGroups({
      eventType: 'confirm_extra_data',
      eventProperties: {
        ...this.gtmCommonInfo,
        'input_type': inputType,
      }
    })
  }

  loadBiz() {
    this.loadingStatus.getBiz = true
    this.getBiz()
      .catch((error) => {
        const {ts} = this
        this.updateError({
          id: 'get-biz',
          message: `${ts('biz')} ${ts('info')}를 불러오는데 ${ts('failed')}하였습니다.`
        })
        throw error
      })
      .finally(() => {
        this.loadingStatus.getBiz = false
      })
  }

  loadClient() {
    const {clientId} = this
    if(!clientId) {
      this.loadingStatus.getClient = false
      return
    }
    this.loadingStatus.getClient = true
    this.getClient({clientId})
      .catch((error) => {
        const {ts} = this
        this.updateError({
          id: 'get-client',
          message: `${ts('client')} ${ts('info')}를 불러오는데 ${ts('failed')}하였습니다.`
        })
        throw error
      })
      .finally(() => {
        this.loadingStatus.getClient = false
      })
  }

  onGetPreview() {
    const type = this.isMobile ? 'png' : 'html'
    this.getPreview({...this.issuePayload, type})
      .then((response) => {
        this.previewData = response
        this.showPreview()
        this.logEventWithGroups({
          eventType: 'view_preview',
          eventProperties: {
            ...this.gtmCommonInfo,
          }
        })
      })
      .catch((error) => {
        this.updateError({
          id: 'get-preview',
          message: `${this.ts('preview')} 이미지를 가져오는데 ${this.ts('failed')}하였습니다.`,
        })
        throw error
      })
  }

  onComplete() {
    this.$router.push({name: 'issued-invoices'})
      .then(() => {
        this.closePopup('done')
      })
      .catch((e) => e)
  }

  openNewWindow(url) {
    window.open(url, '_blank')
  }

  showPreview() {
    if(this.isMobile) {
      this.openPopup('preview')
    } else {
      this.update({previewData: this.previewData})
      this.openNewWindow(`${getBaseUrl(location.href)}/bizs/${this.bizId}/issue-document`)
    }
  }
}
